import { Dayjs } from 'dayjs'
import { ConditionalKeys } from 'type-fest'

import { getOnlyDate, isBetween, isSameDate } from '~/astra'
import { ThreadSelection } from '~/components/RichTextEditing/useRichTextThread'
import { isLocationDateSelection, isScheduleDateSelection } from '~/store/slices/selectionDrawerSlice'
import { StoreState } from '~/store/store'

import { CommentsThread, Include, selectCommentsThread, selectCommentsThreads, useCommentsThreads } from '../'

export type ThreadRefType = CommentsThread['ref']['attribute_type']
export type ThreadRef = CommentsThread['ref']

export type ScheduleDateThreadRef = Extract<ThreadRef, { attribute_type: 'schedule_date_thread_ref' }>
export type LocationScheduleThreadRef = Extract<ThreadRef, { attribute_type: 'location_schedule_thread_ref' }>

export type ScheduleDateThread = Omit<CommentsThread, 'ref'> & { ref: ScheduleDateThreadRef }
export type LocationScheduleThread = Omit<CommentsThread, 'ref'> & { ref: LocationScheduleThreadRef }

export const isScheduleThreadRef = (threadRef: ThreadRef): threadRef is ScheduleDateThreadRef | LocationScheduleThreadRef =>
    threadRef.attribute_type === 'schedule_date_thread_ref' || threadRef.attribute_type === 'location_schedule_thread_ref'

export const isScheduleDateThread = (thread: CommentsThread): thread is ScheduleDateThread => thread.ref.attribute_type === 'schedule_date_thread_ref'

export const isLocationScheduleThread = (thread: CommentsThread): thread is LocationScheduleThread =>
    thread.ref.attribute_type === 'location_schedule_thread_ref'

export const isScheduleThread = (thread: CommentsThread): thread is ScheduleDateThread | LocationScheduleThread => isScheduleThreadRef(thread.ref)

export function isThreadRefForSelection(selection: ThreadSelection, threadRef: CommentsThread['ref']): boolean {
    if (selection.type === 'planningPeriod') {
        return threadRef.attribute_type === 'planning_period_thread_ref' && threadRef.planning_period_id === selection.planningPeriodId
    }
    if (threadRef.attribute_type === 'planning_period_thread_ref') {
        return false
    }
    const dateEqual = threadRef.schedule_date === getOnlyDate(selection.date)
    if (!dateEqual) {
        return false
    }
    if (threadRef.attribute_type === 'location_schedule_thread_ref' && isLocationDateSelection(selection)) {
        return threadRef.location_id === selection.locationId
    }
    return threadRef.attribute_type === 'schedule_date_thread_ref' && isScheduleDateSelection(selection)
}

type IncludeValue = 'include' | true | false
type BooleanThreadProperties = ConditionalKeys<CommentsThread, boolean>
export type CommentThreadFilterOptions = Partial<Record<BooleanThreadProperties, IncludeValue>>

const defaultFilterOptions: CommentThreadFilterOptions = {
    resolved: false,
    archived: false,
    pinned: false,
}

function mergeFilterOptions(filterOptions?: CommentThreadFilterOptions): CommentThreadFilterOptions {
    return { ...defaultFilterOptions, ...filterOptions }
}

function matchesFilterOptions(thread: CommentsThread, filterOptions?: CommentThreadFilterOptions): boolean {
    const merged = mergeFilterOptions(filterOptions)
    const propertiesToApply = Object.entries(merged).filter(([, value]) => value !== 'include')
    return propertiesToApply.every(([property, value]) => thread[property as keyof CommentsThread] === value)
}

function toStormWhere(filterOptions?: CommentThreadFilterOptions) {
    const merged = mergeFilterOptions(filterOptions)
    const where: Record<string, boolean> = {}
    for (const [property, value] of Object.entries(merged)) {
        if (value !== 'include') {
            where[property] = value
        }
    }
    return where
}

export const COMMENTS_THREAD_LIST_INCLUDE = {
    messages: { reactions: true },
    reactions: true,
} as const

export type PopulatedCommentsThread = Include<CommentsThread, typeof COMMENTS_THREAD_LIST_INCLUDE>

export function filterCommentThreadsBySelection(
    threads: readonly CommentsThread[],
    selection: ThreadSelection,
    filterOptions?: CommentThreadFilterOptions
): CommentsThread[] {
    return threads.filter(thread => isThreadRefForSelection(selection, thread.ref) && matchesFilterOptions(thread, filterOptions))
}

export function filterLocationScheduleThreadsByDate(
    threads: readonly CommentsThread[],
    date: Dayjs | string,
    filterOptions?: CommentThreadFilterOptions
): LocationScheduleThread[] {
    return threads.filter(isLocationScheduleThread).filter(thread => isSameDate(thread.ref.schedule_date, date) && matchesFilterOptions(thread, filterOptions))
}

export function filterCommentThreadsByDateRange(
    threads: readonly CommentsThread[],
    startDate: Dayjs | string,
    endDate: Dayjs | string,
    filterOptions?: CommentThreadFilterOptions
): CommentsThread[] {
    return threads.filter(thread => {
        if (!isScheduleThread(thread)) {
            return false
        }
        const scheduleDate = getOnlyDate(thread.ref.schedule_date)
        return isBetween(scheduleDate, startDate, endDate, 'day', '[]') && matchesFilterOptions(thread, filterOptions)
    })
}

function useActiveCommentThreads() {
    return useCommentsThreads({ where: toStormWhere({ pinned: 'include' }) })
}

export function useCommentThreadsBySelection(selection: ThreadSelection, filterOptions?: CommentThreadFilterOptions) {
    const threads = useCommentsThreads({
        where: toStormWhere(filterOptions),
        include: COMMENTS_THREAD_LIST_INCLUDE,
    })

    return filterCommentThreadsBySelection(threads, selection, filterOptions)
}

export function useLocationScheduleThreadsByDate(date: Dayjs | string | undefined, filterOptions?: CommentThreadFilterOptions) {
    const threads = useCommentsThreads({
        where: toStormWhere(filterOptions),
        include: COMMENTS_THREAD_LIST_INCLUDE,
    })

    if (!date) {
        return []
    }

    return filterLocationScheduleThreadsByDate(threads, date, filterOptions)
}

export function useCommentCountBySelection(selection: ThreadSelection): number {
    const threads = useActiveCommentThreads()
    return threads.filter(thread => isThreadRefForSelection(selection, thread.ref)).length
}

export function useLocationDateCommentCount(date: Dayjs, locationId: number): number {
    return useCommentCountBySelection({ type: 'locationDate', date, locationId })
}

export function useCommentCountByDateRange(startDate: Dayjs, endDate: Dayjs): number {
    const threads = useActiveCommentThreads()
    return filterCommentThreadsByDateRange(threads, startDate, endDate, { pinned: 'include' }).length
}

export function selectCommentThreadsBySelection(
    state: StoreState,
    selection: ThreadSelection,
    filterOptions?: CommentThreadFilterOptions
): PopulatedCommentsThread[] {
    const threads = selectCommentsThreads(state, {
        where: toStormWhere(filterOptions),
        include: COMMENTS_THREAD_LIST_INCLUDE,
    })

    return filterCommentThreadsBySelection(threads, selection, filterOptions)
}

export function selectCommentThreadById(state: StoreState, id: number): PopulatedCommentsThread | undefined {
    return selectCommentsThread(state, {
        where: { id },
        include: COMMENTS_THREAD_LIST_INCLUDE,
    })
}
